import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * GoodLot rule:
 * - Homepage / collection / quick-add keep their existing Shopify/Horizon openCart flow.
 * - ONLY the actual product page gets the custom delayed auto-open below.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {Animation | null} */
  #mobileOpenAnimation = null;

  /** @type {number} */
  #savingsAnimationFrame = 0;

  /** @type {number} */
  #savingsCelebrationTimer = 0;

  /** Product-page-only delay before opening the cart drawer. */
  static #PRODUCT_PAGE_OPEN_DELAY = 700;

  /** Savings number-roll duration. */
  static #SAVINGS_ROLL_DURATION = 1200;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    this.#mobileOpenAnimation?.cancel();
    this.#mobileOpenAnimation = null;

    if (this.#savingsAnimationFrame) {
      cancelAnimationFrame(this.#savingsAnimationFrame);
      this.#savingsAnimationFrame = 0;
    }

    window.clearTimeout(this.#savingsCelebrationTimer);
    this.#savingsCelebrationTimer = 0;
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#animateMobileOpen();

    // Run after the drawer has entered the open state so the current,
    // section-rendered savings row is available in the live DOM.
    requestAnimationFrame(() => this.#animateSavingsCelebration());

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * GoodLot mobile entrance motion.
   * Animates only the drawer content, never the <dialog> lifecycle itself.
   */
  #animateMobileOpen() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const dialog = this.#dialog;
    const inner = /** @type {HTMLElement | null} */ (dialog?.querySelector('.cart-drawer__inner'));

    if (!dialog?.open || !inner) return;

    this.#mobileOpenAnimation?.cancel();

    const animation = inner.animate(
      [
        {
          transform: 'translate3d(0, 72px, 0)',
          opacity: 0.96,
        },
        {
          transform: 'translate3d(0, 0, 0)',
          opacity: 1,
        },
      ],
      {
        duration: 320,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      }
    );

    this.#mobileOpenAnimation = animation;

    animation.finished
      .catch(() => {})
      .finally(() => {
        if (this.#mobileOpenAnimation === animation) {
          animation.cancel();
          this.#mobileOpenAnimation = null;
        }
      });
  }

  /**
   * Rolls the savings amount from $0.00 to the current cart savings and
   * triggers a short, restrained confetti burst each time the cart opens.
   */
  #animateSavingsCelebration() {
  /*
   * Empty cart -> first item is a special case:
   * Shopify may replace the cart drawer content before/while the drawer opens.
   *
   * Always target the CURRENT live cart-drawer component instead of assuming
   * that "this" is still the component containing the latest cart markup.
   */
  const liveComponent =
    /** @type {HTMLElement | null} */ (
      document.querySelector(
        'theme-drawer#cart-drawer cart-drawer-component'
      )
    ) || this;

  const savings =
    /** @type {HTMLElement | null} */ (
      liveComponent.querySelector(
        '[data-goodlot-cart-savings]'
      )
    );

  const amount =
    /** @type {HTMLElement | null} */ (
      savings?.querySelector(
        '[data-goodlot-savings-amount]'
      )
    );

  if (!savings || !amount) return;

  /*
   * Used only to prevent the Empty Cart fallback from replaying the same
   * celebration if DrawerOpen already triggered it successfully.
   */
  savings.dataset.goodlotSavingsAnimatedAt =
    String(Date.now());

    const cents = Number(savings.dataset.goodlotSavingsCents || 0);
    const finalText = savings.dataset.goodlotSavingsFinal || amount.textContent?.trim() || '';

    if (!Number.isFinite(cents) || cents <= 0) {
      amount.textContent = finalText;
      return;
    }

    if (this.#savingsAnimationFrame) {
      cancelAnimationFrame(this.#savingsAnimationFrame);
      this.#savingsAnimationFrame = 0;
    }

    window.clearTimeout(this.#savingsCelebrationTimer);

    savings.classList.remove('is-celebrating');
    void savings.offsetWidth;
    savings.classList.add('is-celebrating');

    this.#savingsCelebrationTimer = window.setTimeout(() => {
      savings.classList.remove('is-celebrating');
      this.#savingsCelebrationTimer = 0;
    }, 1650);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      amount.textContent = finalText;
      return;
    }

    const target = cents / 100;
    const locale = document.documentElement.lang || 'en-US';
    const currency = window.Shopify?.currency?.active || 'USD';

    let formatter;

    try {
      formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    amount.textContent = formatter.format(0);

    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / CartDrawerComponent.#SAVINGS_ROLL_DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = target * eased;

      amount.textContent = formatter.format(value);

      if (progress < 1) {
        this.#savingsAnimationFrame = requestAnimationFrame(tick);
        return;
      }

      amount.textContent = finalText;
      this.#savingsAnimationFrame = 0;
    };

    this.#savingsAnimationFrame = requestAnimationFrame(tick);
  }
  /**
 * Empty cart -> first item fallback.
 *
 * When the first product is added, Shopify replaces the Empty Cart markup
 * with the populated cart markup. Depending on timing, DrawerOpen can fire
 * before the new savings row exists.
 *
 * Wait briefly for BOTH:
 * 1. the live cart drawer to be open
 * 2. the new savings row to exist
 *
 * Then replay the existing savings animation.
 */
#animateSavingsWhenReady(attempt = 0) {
  const liveDrawer =
    this.#getLiveCartDrawer() ||
    this.#themeDrawer;

  const liveComponent =
    /** @type {HTMLElement | null} */ (
      document.querySelector(
        'theme-drawer#cart-drawer cart-drawer-component'
      )
    );

  const savings =
    /** @type {HTMLElement | null} */ (
      liveComponent?.querySelector(
        '[data-goodlot-cart-savings]'
      )
    );

  const drawerIsOpen =
    Boolean(liveDrawer?.isOpen);

  if (drawerIsOpen && savings) {
    /*
     * DrawerOpen may already have successfully animated the NEW DOM.
     * In that case don't fire the celebration twice.
     */
    const lastAnimatedAt =
      Number(
        savings.dataset.goodlotSavingsAnimatedAt ||
        0
      );

    if (
      !lastAnimatedAt ||
      Date.now() - lastAnimatedAt > 900
    ) {
      this.#animateSavingsCelebration();
    }

    return;
  }

  /*
   * Up to ~800ms waiting for Shopify's section-rendered cart DOM.
   */
  if (attempt >= 16) return;

  window.setTimeout(() => {
    this.#animateSavingsWhenReady(
      attempt + 1
    );
  }, 50);
}

  #getLiveCartDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      document.querySelector('theme-drawer#cart-drawer')
    );
  }

  #openLiveCartDrawer() {
    const drawer = this.#getLiveCartDrawer() || this.#themeDrawer;

    if (drawer && !drawer.isOpen) {
      drawer.open();
    }
  }

  #isProductPage() {
    return (
      document.body.classList.contains('template-product') ||
      /\/products\//.test(window.location.pathname)
    );
  }

  #wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  /**
   * IMPORTANT:
   *
   * Homepage / collection / quick-add:
   * This component does NOT call open() at all.
   * Their already-existing Shopify/Horizon openCart flow owns opening the cart.
   *
   * Product page:
   * This component owns the special 700ms delayed open that was added to fix
   * the product-page cart not appearing after the fly-to-cart animation.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
  const settle = () =>
    requestAnimationFrame(
      () => this.#updateStickyState()
    );

  /*
   * Detect whether this add started from Empty Cart.
   *
   * If a savings row already exists, normal DrawerOpen behavior is enough
   * and we do NOT add another animation trigger.
   */
  const wasEmpty =
    !this.querySelector(
      '[data-goodlot-cart-savings]'
    );

  const shouldReplayFirstAddSavings =
    event.action === 'add' &&
    wasEmpty;

  /*
   * NON-PRODUCT PAGES
   *
   * Homepage / collection / quick-add continue using their existing
   * Shopify/Horizon cart-opening behavior.
   */
  if (!this.#isProductPage()) {
    event.promise
      ?.then(() => {
        settle();

        if (
          shouldReplayFirstAddSavings
        ) {
          this.#animateSavingsWhenReady();
        }
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });

    return;
  }

  /*
   * PRODUCT PAGE ONLY
   */
  const shouldAutoOpen =
    this.hasAttribute('auto-open') &&
    event.action === 'add' &&
    !this.#themeDrawer?.isOpen;

  const sourceModal =
    /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest(
            'dialog:modal'
          )
        : null
    );

  if (!shouldAutoOpen) {
    event.promise
      ?.then(() => {
        settle();

        if (
          shouldReplayFirstAddSavings
        ) {
          this.#animateSavingsWhenReady();
        }
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });

    return;
  }

  const delayPromise =
    this.#wait(
      CartDrawerComponent
        .#PRODUCT_PAGE_OPEN_DELAY
    );

  const cartResultPromise =
    event.promise
      ? event.promise.then(
          ({ detail }) =>
            !detail?.didError
        )
      : Promise.resolve(true);

  Promise.all([
    delayPromise,
    cartResultPromise,
  ])
    .then(
      ([, cartSucceeded]) => {
        if (!cartSucceeded) {
          settle();
          return;
        }

        const openAndSettle =
          () => {
            this.#openLiveCartDrawer();

            settle();

            /*
             * Important only for:
             * Empty Cart -> first product.
             *
             * Wait until Shopify has actually inserted
             * the populated-cart savings row, then run
             * the SAME existing savings animation.
             */
            if (
              shouldReplayFirstAddSavings
            ) {
              this.#animateSavingsWhenReady();
            }
          };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            {
              once: true,
            }
          );
        } else {
          openAndSettle();
        }
      }
    )
    .catch((error) => {
      if (
        error?.name !== 'AbortError'
      ) {
        console.warn(
          '[cart-drawer] Product-page auto-open failed:',
          error
        );
      }
    });
};

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}